import React from 'react';
import { getMeasurementDisplayName } from '../../../constants/measurementLabels';
import { useTranslation } from 'react-i18next';

interface ProceedConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  viewName: string;
  frameNumber?: number;
  measurements?: Array<{
    id: string;
    meas_name: string;
    value: number;
  }>;
}

export const ProceedConfirmationModal: React.FC<
  ProceedConfirmationModalProps
> = ({
  isOpen,
  onClose,
  onProceed,
  viewName,
  frameNumber,
  measurements = [],
}) => {
  const { t } = useTranslation(['ViewDetails', 'GeneralViewer']);
  if (!isOpen) {
    return null;
  }

  const handleProceed = () => {
    onProceed();
    onClose();
  };

  const formatMeasurementName = (measName: string) =>
    getMeasurementDisplayName(measName);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('proceedModal.title', { defaultValue: 'Proceed as View' })}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t('proceedModal.subtitle', {
                  defaultValue: 'Add this frame to the report for {{viewName}}',
                  viewName,
                })}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg mb-4">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-sm text-blue-800 dark:text-blue-400 font-medium">
              {t('proceedModal.frameWillBeAdded', {
                defaultValue:
                  'Frame {{frameNumber}} will be added to the report',
                frameNumber,
              })}
            </span>
          </div>

          {measurements.length > 0 && (
            <div className="border rounded-lg p-3 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-700 mb-4">
              <div className="text-xs font-semibold text-green-800 dark:text-green-300 mb-2 uppercase tracking-wide">
                {t('proceedModal.measurementsIncluded', {
                  defaultValue: 'Measurements to be included',
                })}
              </div>
              <div className="space-y-1">
                {measurements.map((measurement, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {formatMeasurementName(measurement.meas_name)}
                    </span>
                    <span className="text-sm font-bold text-green-700 dark:text-green-400">
                      {measurement.value.toFixed(2)} cm
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-4">
            <span className="text-sm text-amber-800 dark:text-amber-400 font-medium">
              {t('proceedModal.reportWillUpdate', {
                defaultValue: 'This action will update the report.',
              })}
            </span>
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md font-medium transition-colors"
            >
              {t('actions.cancel', {
                ns: 'ViewDetails',
                defaultValue: 'Cancel',
              })}
            </button>
            <button
              onClick={handleProceed}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-md font-medium transition-colors"
            >
              {t('proceedModal.cta', { defaultValue: 'Proceed as View' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
